import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { setDeviceSchedule } from '../../api_calls/devices';

const ScheduleDeviceModal = ({ open, onClose, device, schedule, onSuccess }) => {
    const [time, setTime] = useState(dayjs().hour(20).minute(0));
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(30);
    const [daily, setDaily] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (schedule) {
                // Editing existing schedule
                const scheduleTime = dayjs(schedule.timestamp);
                setTime(scheduleTime);
                setDaily(schedule.recurring || false);

                // Parse duration (e.g., "1m", "30m", "2h", "+1m", "+30m")
                const duration = schedule.duration || '0m';
                const cleanDuration = duration.replace(/^\+/, ''); // Remove leading +
                if (cleanDuration.includes('h')) {
                    const h = parseInt(cleanDuration.replace('h', ''));
                    setHours(h);
                    setMinutes(0);
                } else if (cleanDuration.includes('m')) {
                    const m = parseInt(cleanDuration.replace('m', ''));
                    setHours(Math.floor(m / 60));
                    setMinutes(m % 60);
                } else {
                    setHours(0);
                    setMinutes(0);
                }
            } else {
                // New schedule - reset to defaults
                setTime(dayjs().hour(20).minute(0));
                setHours(0);
                setMinutes(30);
                setDaily(false);
            }
            setError(null);
        }
    }, [open, schedule]);

    const handleSave = () => {
        if (!device?.device_id) {
            setError('Device ID is missing');
            return;
        }

        setLoading(true);
        setError(null);

        // Format time as ISO string (YYYY-MM-DDTHH:mm)
        // For daily schedules, use today's date with selected time
        // For non-daily, use the selected date and time
        let formattedTime;
        if (daily) {
            // For daily schedules, use today's date with the selected time
            formattedTime = dayjs().hour(time.hour()).minute(time.minute()).format('YYYY-MM-DDTHH:mm');
        } else {
            // For one-time schedules, use the selected date and time
            formattedTime = time.format('YYYY-MM-DDTHH:mm');
        }

        // Calculate total duration in minutes
        const totalMinutes = hours * 60 + minutes;
        const duration = `+${totalMinutes}m`;

        // If editing and disabling, set enable to false
        const enable = true; // Always enable when saving (use Clear Schedule to disable)

        const scheduleData = {
            start: formattedTime,
            duration: duration,
            daily: daily,
            enable: enable
        };

        setDeviceSchedule(device.device_id, scheduleData)
            .then(() => {
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            })
            .catch((err) => {
                console.error('Error setting schedule:', err);
                setError('Failed to save schedule. Please try again.');
                setLoading(false);
            });
    };

    const handleClearSchedule = () => {
        if (!device?.device_id || !schedule) {
            setError('Cannot clear schedule');
            return;
        }

        setLoading(true);
        setError(null);

        // To delete, we set enable to false
        const scheduleData = {
            start: schedule.timestamp,
            duration: schedule.duration || '+1m',
            daily: schedule.recurring || false,
            enable: false
        };

        setDeviceSchedule(device.device_id, scheduleData)
            .then(() => {
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            })
            .catch((err) => {
                console.error('Error clearing schedule:', err);
                setError('Failed to clear schedule. Please try again.');
                setLoading(false);
            });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Schedule Device</Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {device?.name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {device.name}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ mb: 3, mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AccessTimeIcon sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Turn on at
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {daily ? (
                                <TimePicker
                                    value={time}
                                    onChange={(newValue) => setTime(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            sx={{ flex: 1 }}
                                        />
                                    )}
                                />
                            ) : (
                                <DateTimePicker
                                    value={time}
                                    onChange={(newValue) => setTime(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            sx={{ flex: 1 }}
                                        />
                                    )}
                                />
                            )}
                            <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Turn off after
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Hours</InputLabel>
                                <Select
                                    value={hours}
                                    label="Hours"
                                    onChange={(e) => setHours(e.target.value)}
                                >
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <MenuItem key={i} value={i}>
                                            {i}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Minutes</InputLabel>
                                <Select
                                    value={minutes}
                                    label="Minutes"
                                    onChange={(e) => setMinutes(e.target.value)}
                                >
                                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={daily}
                                onChange={(e) => setDaily(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Everyday"
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
                    {schedule && (
                        <Button
                            onClick={handleClearSchedule}
                            color="error"
                            fullWidth
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            Clear Schedule
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button
                            onClick={onClose}
                            disabled={loading}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={loading}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            {schedule ? 'Update Schedule' : 'Set Schedule'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default ScheduleDeviceModal;

